import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const VirtualTwin = () => {
  const [loading, setLoading] = useState(false);
  const [simulationData, setSimulationData] = useState({
    health_trajectory: [],
    habit_trajectory: [],
    wellness_trajectory: []
  });

  const runSimulation = async () => {
    setLoading(true);
    try {
      // Simulation logic would go here
      setSimulationData({
        health_trajectory: [
          { month: 'Jan', brain: 75, heart: 82, liver: 68, lung: 90 },
          { month: 'Feb', brain: 77, heart: 84, liver: 70, lung: 88 },
          { month: 'Mar', brain: 79, heart: 86, liver: 72, lung: 92 },
          { month: 'Apr', brain: 81, heart: 88, liver: 74, lung: 94 },
          { month: 'May', brain: 83, heart: 90, liver: 76, lung: 96 },
          { month: 'Jun', brain: 85, heart: 92, liver: 78, lung: 98 }
        ],
        habit_trajectory: [
          { week: 'W1', completion_rate: 65, streak: 3 },
          { week: 'W2', completion_rate: 72, streak: 5 },
          { week: 'W3', completion_rate: 78, streak: 7 },
          { week: 'W4', completion_rate: 85, streak: 10 },
          { week: 'W5', completion_rate: 88, streak: 12 },
          { week: 'W6', completion_rate: 92, streak: 15 }
        ],
        wellness_trajectory: [
          { day: 'Mon', mood: 6.5, stress: 4.2, energy: 7.1 },
          { day: 'Tue', mood: 7.2, stress: 3.8, energy: 7.8 },
          { day: 'Wed', mood: 6.8, stress: 4.5, energy: 6.9 },
          { day: 'Thu', mood: 7.5, stress: 3.2, energy: 8.2 },
          { day: 'Fri', mood: 8.1, stress: 2.8, energy: 8.9 },
          { day: 'Sat', mood: 7.8, stress: 3.5, energy: 8.0 },
          { day: 'Sun', mood: 7.3, stress: 3.9, energy: 7.6 }
        ]
      });
    } catch (error) {
      console.error('Failed to run simulation:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runSimulation();
  }, []);

  return (
    <div className="max-w-7xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Virtual Health Twin</h1>
        <p className="text-gray-600">AI-powered simulation of your health and wellness journey</p>
      </div>

      {/* Simulation Controls */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Digital Twin Simulation</h2>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
          <button
            onClick={runSimulation}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? <LoadingSpinner size="small" /> : 'Run New Simulation'}
          </button>
        </div>
      </div>

      {/* Health Trajectory */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">6-Month Health Projection</h3>
          <div className="space-y-4">
            {['brain', 'heart', 'liver', 'lung'].map((organ) => {
              const currentData = simulationData.health_trajectory[simulationData.health_trajectory.length - 1];
              const currentValue = currentData?.[organ] || 75;
              const projectedValue = currentValue + ((Math.random() * 10) - 5); // Mock projection
              
              return (
                <div key={organ} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="capitalize font-medium">{organ}</span>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Current</div>
                    <div className="font-semibold">{currentValue}%</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Projected</div>
                    <div className={`font-semibold ${projectedValue > currentValue ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.round(projectedValue)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Habit Success Prediction</h3>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Overall Success Rate</span>
                <span className="text-2xl font-bold text-green-600">87%</span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: '87%' }}></div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">92%</div>
                <div className="text-sm text-gray-600">Morning Habits</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">78%</div>
                <div className="text-sm text-gray-600">Evening Habits</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wellness Insights */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">AI Insights & Recommendations</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl mb-2">🧠</div>
            <h4 className="font-semibold text-blue-900 mb-2">Cognitive Health</h4>
            <p className="text-sm text-gray-600 mb-3">
              Your mental clarity is improving. Continue current sleep and stress management practices.
            </p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Maintain 7-8 hours sleep</li>
              <li>• Practice daily meditation</li>
              <li>• Regular brain exercises</li>
            </ul>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-3xl mb-2">💪</div>
            <h4 className="font-semibold text-green-900 mb-2">Physical Wellness</h4>
            <p className="text-sm text-gray-600 mb-3">
              Your physical health trajectory is positive. Focus on consistency in exercise and nutrition.
            </p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• 30 mins daily exercise</li>
              <li>• Balanced nutrition</li>
              <li>• Regular health checkups</li>
            </ul>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="text-3xl mb-2">🎯</div>
            <h4 className="font-semibold text-purple-900 mb-2">Recovery Strength</h4>
            <p className="text-sm text-gray-600 mb-3">
              Your addiction recovery is progressing well. Build on current momentum and support systems.
            </p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Strong support network</li>
              <li>• Effective coping strategies</li>
              <li>• Relapse prevention plan</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualTwin;
